import { useQuery } from "@tanstack/react-query";
import { GET_OPPORTUNITY_RESOURCE_DATA } from "@/services/constants";
import { useDateFromSearchParams } from "@/helpers/useTimeHooks";
import { getDatabricksResourceOpportunities } from "@/helpers/apis";
import { ResourceType } from "@/pages/explorer/types";
import {
  getDateByEndOfDayWithoutTimeoffset,
  getDateByStartOfDayWithoutTimeoffset,
} from "@/components/dateRange/utils";
import { useInsightState } from "@/modules/insights/context";

export { useDateFromSearchParams };

export const useResourceOpportunities = ({
  resourceUri,
  resourceType,
  instanceId,
  workspaceId,
  startDate: startDateProp,
  endDate: endDateProp,
}: {
  resourceUri: string;
  resourceType: ResourceType;
  instanceId?: string;
  workspaceId?: string;
  /** Dates to fetch with; defaults to the unprefixed URL date range. */
  startDate?: Date;
  endDate?: Date;
}) => {
  const fallbackDates = useDateFromSearchParams();
  const startDate = startDateProp ?? fallbackDates.startDate;
  const endDate = endDateProp ?? fallbackDates.endDate;
  const {
    selectedTab: status,
    sort: { sortBy, sortOrder },
  } = useInsightState();

  return useQuery({
    queryKey: [
      GET_OPPORTUNITY_RESOURCE_DATA,
      resourceUri,
      resourceType,
      instanceId,
      workspaceId,
      startDate,
      endDate,
      status,
      sortBy,
      sortOrder,
    ],
    queryFn: () =>
      getDatabricksResourceOpportunities({
        resource_uri: resourceUri,
        resource_type: resourceType,
        instance_id: instanceId || "",
        ...(workspaceId && { workspace_id: workspaceId }),
        start_date: getDateByStartOfDayWithoutTimeoffset(startDate).toISOString(),
        end_date: getDateByEndOfDayWithoutTimeoffset(endDate)!.toISOString(),
        // Backend reads ``status_filter`` — the prior ``status`` key was
        // silently dropped by FastAPI, so the InsightSection filter tabs had
        // no effect across cluster/warehouse/job/query opportunities pages.
        ...(status && status.length > 0 && { status_filter: status.join(",") }),
        ...(sortBy && { sort: sortBy }),
        ...(sortOrder && { sort_order: sortOrder }),
      }),
    enabled: Boolean(resourceUri),
  });
};
